/**
 * --------------------------------------------------------------------------
 * Popover Element Custom Web Component
 * --------------------------------------------------------------------------
 * A drop-in replacement for the native Popover API.
 * Provides showPopover(), hidePopover(), togglePopover() methods and
 * :popover-open pseudo-class support via matches().
 * 
 * This is used as a fallback when the native Popover API causes issues
 * with certain third-party applications.
 */

// Polyfill for ToggleEvent (for older browsers)
if (typeof ToggleEvent === 'undefined') {
    window.ToggleEvent = class ToggleEvent extends Event {
        constructor(type, eventInitDict = {}) {
            super(type, eventInitDict);
            this.oldState = eventInitDict.oldState || '';
            this.newState = eventInitDict.newState || '';
        }
    };
}

if (!customElements.get('popover-element')) {
    class PopoverElement extends HTMLElement {
        constructor() {
            super();
            this._isOpen = false;
        }

        connectedCallback() {
            // Ensure the element has a role for accessibility
            if (!this.hasAttribute('role')) {
                this.setAttribute('role', 'dialog');
            }

            // Initialize as closed using class + inline style approach:
            // 1. Add inline style="display: none" (overrides CSS, keeps it hidden)
            // 2. Add .popover-initialized class (CSS sets display: block)
            // When showing: remove inline style → CSS display: block takes effect
            // When hiding: add inline style="display: none" → overrides CSS
            this._isOpen = false;
            this.style.display = 'none';
            this.classList.add('popover-initialized');
        }

        disconnectedCallback() {
            // Clean up - ensure closed state
            this._isOpen = false;
            this.classList.remove('popover-initialized');
        }

        /**
         * Shows the popover element
         */
        showPopover() {
            if (this._isOpen) return; // Already open
            
            // Dispatch beforetoggle event
            const beforeToggleEvent = new ToggleEvent('beforetoggle', {
                oldState: 'closed',
                newState: 'open',
                bubbles: false,
                composed: false,
            });
            this.dispatchEvent(beforeToggleEvent);

            // Make visible by removing inline style override
            // CSS .popover-initialized { display: block } takes effect
            this._isOpen = true;
            this.style.display = '';
            this.classList.add('popover-open');

            // Force reflow to ensure CSS transitions work if needed
            void this.offsetHeight;
            
            // Dispatch toggle event
            const toggleEvent = new ToggleEvent('toggle', {
                oldState: 'closed',
                newState: 'open',
                bubbles: false,
                composed: false,
            });
            this.dispatchEvent(toggleEvent);
        }

        /**
         * Hides the popover element
         */
        hidePopover() {
            if (!this._isOpen) return; // Already closed
            
            // Dispatch beforetoggle event
            const beforeToggleEvent = new ToggleEvent('beforetoggle', {
                oldState: 'open',
                newState: 'closed',
                bubbles: false,
                composed: false,
            });
            this.dispatchEvent(beforeToggleEvent);

            // Hide the element by adding inline style override
            // Inline style="display: none" overrides CSS display: block
            this._isOpen = false;
            this.style.display = 'none';
            this.classList.remove('popover-open');
            
            // Dispatch toggle event
            const toggleEvent = new ToggleEvent('toggle', {
                oldState: 'open',
                newState: 'closed',
                bubbles: false,
                composed: false,
            });
            this.dispatchEvent(toggleEvent);
        }

        /**
         * Toggles the popover visibility
         */
        togglePopover(force) {
            // If force is a boolean, use it to determine action
            if (typeof force === 'boolean') {
                if (force && !this._isOpen) {
                    this.showPopover();
                } else if (!force && this._isOpen) {
                    this.hidePopover();
                }
            } else {
                // Toggle based on current state
                if (this._isOpen) {
                    this.hidePopover();
                } else {
                    this.showPopover();
                }
            }
        }

        /**
         * Checks if the popover matches a CSS selector
         * Provides support for :popover-open pseudo-class via matches()
         */
        matches(selector) {
            // Check if selector is :popover-open and element is open
            if (selector === ':popover-open') {
                return this._isOpen;
            }
            
            // For other selectors, use the native matches method
            return super.matches(selector);
        }

        /**
         * Gets the current popover state ('open' or 'closed')
         */
        get popoverState() {
            return this._isOpen ? 'open' : 'closed';
        }

        /**
         * Checks if the popover is currently open
         */
        get isOpen() {
            return this._isOpen;
        }
    }

    customElements.define('popover-element', PopoverElement);
}

